import React, { useEffect, useState, useRef } from 'react';
import RichTextEditor from './RichTextEditor';

// Two-way thread between an admin and an applicant. Used both inside the
// admin drawer (mode='admin' + applicationId) and on the customer Apply
// page (mode='customer'). Polls every 30s while mounted so a fresh reply
// shows up without a hard refresh.

const tokenHdr = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const adminHdr = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}`,
});

export default function ApplicationMessages({ mode = 'customer', applicationId }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const endRef = useRef(null);

  const endpoint = mode === 'admin'
    ? { list: `/api/applications/admin/${applicationId}/messages`,
        post: `/api/applications/admin/${applicationId}/messages`,
        headers: adminHdr() }
    : { list: '/api/applications/me/messages',
        post: '/api/applications/me/messages',
        headers: tokenHdr() };

  const load = async () => {
    try {
      const r = await fetch(endpoint.list, { headers: endpoint.headers });
      if (r.ok) {
        const j = await r.json();
        setMessages(j.messages || []);
      }
    } catch (_) {}
  };

  useEffect(() => {
    if (mode === 'admin' && !applicationId) return;
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    setErr(null);
    if (!draft.trim() || draft === '<br>') return;
    setBusy(true);
    try {
      const r = await fetch(endpoint.post, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...endpoint.headers },
        body: JSON.stringify({ body_html: draft }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j.error || 'Send failed');
      } else {
        setDraft('');
        load();
      }
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{
        maxHeight: 320, overflowY: 'auto', padding: 12,
        background: 'var(--bg-hover, #f8fafc)', borderRadius: 6,
        border: '1px solid var(--border-color, #e2e8f0)',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {messages.length === 0 && (
          <p style={{ color: 'var(--text-light, #64748b)', fontSize: 13, textAlign: 'center', margin: 24 }}>
            {mode === 'admin' ? 'No messages yet — send the applicant a question or note.' : 'No messages yet. Send a note to the team if you need to clarify anything.'}
          </p>
        )}
        {messages.map(m => {
          const fromMe = (mode === 'admin' && m.author_type === 'admin') ||
                         (mode === 'customer' && m.author_type === 'customer');
          return (
            <div key={m.id} style={{
              alignSelf: fromMe ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              background: fromMe ? '#3b4cca' : '#fff',
              color: fromMe ? '#fff' : '#0f172a',
              padding: '8px 14px', borderRadius: 12,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              fontSize: 14, lineHeight: 1.5,
            }}>
              <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 4, textTransform: 'uppercase' }}>
                {m.author_type === 'admin' ? 'SilverGames team' : 'Applicant'} · {new Date(m.created_at).toLocaleString()}
              </div>
              <div dangerouslySetInnerHTML={{ __html: m.body_html }} />
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} style={{ marginTop: 12 }}>
        <RichTextEditor value={draft} onChange={setDraft} placeholder={mode === 'admin' ? 'Reply to applicant…' : 'Send a note to the team…'} disabled={busy} />
        {err && <div style={{ color: 'var(--color-danger, #dc2626)', fontSize: 13, marginTop: 6 }}>{err}</div>}
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !draft.trim()}>
            {busy ? 'Sending…' : 'Send →'}
          </button>
        </div>
      </form>
    </div>
  );
}
